import apiClient from './client';

// Enums
export enum SupplyType {
  MEDICATION = 'MEDICATION',
  SUPPLEMENT = 'SUPPLEMENT',
  CLEANING = 'CLEANING',
  DISINFECTANT = 'DISINFECTANT',
  BEDDING = 'BEDDING',
  NESTING = 'NESTING',
  TOOLS = 'TOOLS',
  EQUIPMENT = 'EQUIPMENT',
  CONSUMABLES = 'CONSUMABLES',
  FIRST_AID = 'FIRST_AID',
  VITAMINS = 'VITAMINS',
  PROBIOTICS = 'PROBIOTICS',
  OTHER = 'OTHER',
}

export enum SupplyUnit {
  PIECES = 'PIECES',
  BOTTLES = 'BOTTLES',
  BOXES = 'BOXES',
  PACKETS = 'PACKETS',
  TUBES = 'TUBES',
  VIALS = 'VIALS',
  LITERS = 'LITERS',
  MILLILITERS = 'MILLILITERS',
  KILOGRAMS = 'KILOGRAMS',
  GRAMS = 'GRAMS',
  METERS = 'METERS',
  ROLLS = 'ROLLS',
  SETS = 'SETS',
  PAIRS = 'PAIRS',
}

export enum SupplyStatus {
  IN_STOCK = 'IN_STOCK',
  LOW_STOCK = 'LOW_STOCK',
  OUT_OF_STOCK = 'OUT_OF_STOCK',
  EXPIRED = 'EXPIRED',
  DISCONTINUED = 'DISCONTINUED',
}

// Interfaces
export interface SupplyMetadata {
  tags?: string[];
  instructions?: string;
  warnings?: string[];
  activeIngredients?: string[];
  dosage?: string;
  contraindications?: string[];
  sideEffects?: string[];
  storageInstructions?: string;
  manufacturerInfo?: {
    name: string;
    contact?: string;
    website?: string;
  };
  certifications?: string[];
  msdsSheet?: string;
  lastAuditDate?: string;
  auditNotes?: string;
}

export interface Supply {
  id: string;
  name: string;
  type: SupplyType;
  quantity: number;
  unit: SupplyUnit;
  unitPrice: number;
  totalValue: number;
  status: SupplyStatus;
  purchaseDate?: Date;
  expirationDate?: Date;
  supplier?: string;
  batchNumber?: string;
  location?: string;
  description?: string;
  instructions?: string;
  storageConditions?: string;
  minimumQuantity: number;
  reorderPoint: number;
  maxStockLevel?: number;
  averageDailyUsage: number;
  leadTimeDays: number;
  lastAuditQuantity?: number;
  lastAuditDate?: Date;
  lastRestockDate?: Date;
  lastRestockQuantity?: number;
  notes?: string;
  metadata?: SupplyMetadata;
  createdAt: Date;
  updatedAt: Date;
  isExpired: boolean;
  isExpiringSoon: boolean;
  daysUntilExpiry?: number | null;
  stockLevel: 'adequate' | 'low' | 'out' | 'overstocked';
}

export interface CreateSupplyDto {
  name: string;
  type: SupplyType;
  quantity: number;
  unit: SupplyUnit;
  unitPrice: number;
  purchaseDate?: Date;
  expirationDate?: Date;
  supplier?: string;
  batchNumber?: string;
  location?: string;
  description?: string;
  instructions?: string;
  storageConditions?: string;
  minimumQuantity: number;
  reorderPoint: number;
  maxStockLevel?: number;
  averageDailyUsage?: number;
  leadTimeDays?: number;
  notes?: string;
  metadata?: SupplyMetadata;
}

export interface UpdateSupplyDto extends Partial<CreateSupplyDto> {}

export interface GetSuppliesDto {
  search?: string;
  type?: SupplyType;
  status?: SupplyStatus;
  supplier?: string;
  location?: string;
  unit?: SupplyUnit;
  stockLevel?: 'all' | 'low_stock' | 'adequate' | 'overstocked' | 'out_of_stock';
  expiryDateStart?: Date;
  expiryDateEnd?: Date;
  minValue?: number;
  maxValue?: number;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface SuppliesResponse {
  supplies: Supply[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SupplyStats {
  totalItems: number;
  lowStockItems: number;
  outOfStockItems: number;
  expiredItems: number;
  expiringSoonItems: number;
  totalValue: number;
  overstockedItems: number;
  averageDaysUntilExpiry: number;
  itemsByType: Record<SupplyType, number>;
  itemsByStatus: Record<SupplyStatus, number>;
  topSuppliers: Array<{ supplier: string; count: number; totalValue: number }>;
  topLocations: Array<{ location: string; count: number; totalValue: number }>;
}

// Analytics Interfaces
export interface InventoryTrendAnalytics {
  period: string;
  generatedAt: string;
  inventoryTrends: {
    monthlyData: Array<{
      month: string;
      totalItems: number;
      totalValue: number;
      totalQuantity: number;
      averageValue: number;
      lowStockItems: number;
      expiredItems: number;
    }>;
    trends: {
      inventoryGrowth: number;
      quantityGrowth: number;
      itemGrowth: number;
    };
  };
  stockMovements: Array<{
    id: string;
    name: string;
    type: string;
    currentStock: number;
    reorderPoint: number;
    maxStock: number | null;
    stockStatus: string;
    daysUntilReorder: number;
    stockTurnover: number;
  }>;
  turnoverAnalysis: Array<{
    id: string;
    name: string;
    type: string;
    inventoryValue: number;
    turnoverRatio: number;
    daysOnHand: number;
    efficiency: number;
  }>;
  seasonalPatterns: {
    seasonal: {
      spring: number;
      summer: number;
      autumn: number;
      winter: number;
    };
    monthly: Array<{
      month: number;
      monthName: string;
      usage: number;
      percentage: number;
    }>;
    peakSeason: string;
    volatility: number;
  };
  forecasting: Array<{
    id: string;
    name: string;
    type: string;
    forecasts: Array<{
      date: string;
      projectedQuantity: number;
      confidence: number;
    }>;
    nextRestockDate: string;
    optimumOrderQuantity: number;
  }>;
  alerts: Array<{
    type: string;
    severity: 'low' | 'medium' | 'high';
    itemId?: string;
    itemName?: string;
    message: string;
    recommendation: string;
  }>;
  performanceMetrics: {
    inventoryEfficiency: number;
    costEfficiency: number;
    stockAccuracy: number;
    totalInventoryValue: number;
    averageTurnover: number;
    optimizationScore: number;
  };
  summary: {
    totalItemsAnalyzed: number;
    lowStockItems: number;
    overstockedItems: number;
    expiredItems: number;
    totalInventoryValue: number;
  };
}

export interface CostTrendAnalysis {
  period: string;
  totalCost: number;
  totalQuantity: number;
  averageCostPerUnit: number;
  costByType: Record<string, number>;
  costBySupplier: Record<string, number>;
  monthlyTrends: Array<{
    month: string;
    cost: number;
    quantity: number;
    averageUnitCost: number;
    itemsPurchased: number;
  }>;
  growthRates: {
    cost: number;
    quantity: number;
    unitCost: number;
  };
  summary: {
    totalItems: number;
    averageItemValue: number;
    highestValueItem: number;
    lowestValueItem: number;
  };
}

export interface UsagePatternAnalysis {
  period: string;
  usageByType: Record<string, number>;
  usageByLocation: Record<string, number>;
  monthlyUsage: Array<{
    month: string;
    usage: number;
    items: number;
    averageUsagePerItem: number;
  }>;
  peakUsage: {
    month: string;
    usage: number;
    items: number;
  };
  summary: {
    totalUsage: number;
    averageUsagePerItem: number;
    mostUsedType: {
      type: string;
      usage: number;
    };
    mostUsedLocation: {
      location: string;
      usage: number;
    };
  };
}



export interface UpdateSupplyQuantityDto {
  quantity: number;
  reason?: string;
  notes?: string;
}

export interface SupplyAuditDto {
  auditedQuantity: number;
  auditNotes?: string;
  auditDate: Date;
}

// API Functions
export const suppliesApi = {
  // Get all supplies with filtering and pagination
  getSupplies: async (filters: GetSuppliesDto = {}): Promise<SuppliesResponse> => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (value instanceof Date) {
          params.append(key, value.toISOString());
        } else {
          params.append(key, value.toString());
        }
      }
    });

    const queryString = params.toString();
    const url = queryString ? `supplies?${queryString}` : 'supplies';
    const response = await apiClient.get<SuppliesResponse>(url);
    return response.data;
  },

  // Get supply by ID
  getSupply: async (id: string): Promise<Supply> => {
    const response = await apiClient.get<Supply>(`supplies/${id}`);
    return response.data;
  },

  // Create new supply
  createSupply: async (data: CreateSupplyDto): Promise<Supply> => {
    const response = await apiClient.post<Supply>('supplies', data);
    return response.data;
  },

  // Update supply
  updateSupply: async (id: string, data: UpdateSupplyDto): Promise<Supply> => {
    const response = await apiClient.patch<Supply>(`supplies/${id}`, data);
    return response.data;
  },

  // Delete supply
  deleteSupply: async (id: string): Promise<void> => {
    await apiClient.delete(`supplies/${id}`);
  },

  // Update supply quantity
  updateSupplyQuantity: async (id: string, data: UpdateSupplyQuantityDto): Promise<Supply> => {
    const response = await apiClient.patch<Supply>(`supplies/${id}/quantity`, data);
    return response.data;
  },

  // Audit supply
  auditSupply: async (id: string, data: SupplyAuditDto): Promise<Supply> => {
    const response = await apiClient.patch<Supply>(`supplies/${id}/audit`, data);
    return response.data;
  },

  // Get supply statistics
  getStatistics: async (): Promise<SupplyStats> => {
    const response = await apiClient.get<SupplyStats>('supplies/statistics');
    return response.data;
  },

  // Get supplies by type
  getSuppliesByType: async (type: SupplyType): Promise<Supply[]> => {
    const response = await apiClient.get<Supply[]>(`supplies/by-type/${type}`);
    return response.data;
  },

  // Get low stock supplies
  getLowStockSupplies: async (): Promise<Supply[]> => {
    const response = await apiClient.get<Supply[]>('supplies/low-stock');
    return response.data;
  },

  // Get expired supplies
  getExpiredSupplies: async (): Promise<Supply[]> => {
    const response = await apiClient.get<Supply[]>('supplies/expired');
    return response.data;
  },

  // Get expiring soon supplies
  getExpiringSoonSupplies: async (): Promise<Supply[]> => {
    const response = await apiClient.get<Supply[]>('supplies/expiring-soon');
    return response.data;
  },

  // Analytics endpoints
  getInventoryTrendAnalytics: async (startDate?: string, endDate?: string): Promise<InventoryTrendAnalytics> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const queryString = params.toString();
    const url = queryString ? `supplies/analytics/trends?${queryString}` : 'supplies/analytics/trends';
    const response = await apiClient.get<InventoryTrendAnalytics>(url);
    return response.data;
  },

  getCostTrendAnalysis: async (startDate?: string, endDate?: string): Promise<CostTrendAnalysis> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const queryString = params.toString();
    const url = queryString ? `supplies/analytics/cost-trends?${queryString}` : 'supplies/analytics/cost-trends';
    const response = await apiClient.get<CostTrendAnalysis>(url);
    return response.data;
  },

  getUsagePatternAnalysis: async (startDate?: string, endDate?: string): Promise<UsagePatternAnalysis> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const queryString = params.toString();
    const url = queryString ? `supplies/analytics/usage-patterns?${queryString}` : 'supplies/analytics/usage-patterns';
    const response = await apiClient.get<UsagePatternAnalysis>(url);
    return response.data;
  },
};

// Helper functions
export const getSupplyTypeLabel = (type: SupplyType): string => {
  const labels: Record<SupplyType, string> = {
    [SupplyType.MEDICATION]: 'Medication',
    [SupplyType.SUPPLEMENT]: 'Supplement',
    [SupplyType.CLEANING]: 'Cleaning',
    [SupplyType.DISINFECTANT]: 'Disinfectant',
    [SupplyType.BEDDING]: 'Bedding',
    [SupplyType.NESTING]: 'Nesting',
    [SupplyType.TOOLS]: 'Tools',
    [SupplyType.EQUIPMENT]: 'Equipment',
    [SupplyType.CONSUMABLES]: 'Consumables',
    [SupplyType.FIRST_AID]: 'First Aid',
    [SupplyType.VITAMINS]: 'Vitamins',
    [SupplyType.PROBIOTICS]: 'Probiotics',
    [SupplyType.OTHER]: 'Other',
  };
  return labels[type] || type;
};

export const getSupplyUnitLabel = (unit: SupplyUnit): string => {
  const labels: Record<SupplyUnit, string> = {
    [SupplyUnit.PIECES]: 'Pieces',
    [SupplyUnit.BOTTLES]: 'Bottles',
    [SupplyUnit.BOXES]: 'Boxes',
    [SupplyUnit.PACKETS]: 'Packets',
    [SupplyUnit.TUBES]: 'Tubes',
    [SupplyUnit.VIALS]: 'Vials',
    [SupplyUnit.LITERS]: 'Liters',
    [SupplyUnit.MILLILITERS]: 'Milliliters',
    [SupplyUnit.KILOGRAMS]: 'Kilograms',
    [SupplyUnit.GRAMS]: 'Grams',
    [SupplyUnit.METERS]: 'Meters',
    [SupplyUnit.ROLLS]: 'Rolls',
    [SupplyUnit.SETS]: 'Sets',
    [SupplyUnit.PAIRS]: 'Pairs',
  };
  return labels[unit] || unit;
};

export const getSupplyStatusLabel = (status: SupplyStatus): string => {
  const labels: Record<SupplyStatus, string> = {
    [SupplyStatus.IN_STOCK]: 'In Stock',
    [SupplyStatus.LOW_STOCK]: 'Low Stock',
    [SupplyStatus.OUT_OF_STOCK]: 'Out of Stock',
    [SupplyStatus.EXPIRED]: 'Expired',
    [SupplyStatus.DISCONTINUED]: 'Discontinued',
  };
  return labels[status] || status;
};

export const getSupplyStatusColor = (status: SupplyStatus): string => {
  const colors: Record<SupplyStatus, string> = {
    [SupplyStatus.IN_STOCK]: 'green',
    [SupplyStatus.LOW_STOCK]: 'yellow',
    [SupplyStatus.OUT_OF_STOCK]: 'red',
    [SupplyStatus.EXPIRED]: 'red',
    [SupplyStatus.DISCONTINUED]: 'gray',
  };
  return colors[status] || 'gray';
};

export const getStockLevelColor = (stockLevel: string): string => {
  const colors: Record<string, string> = {
    adequate: 'green',
    low: 'yellow',
    out: 'red',
    overstocked: 'blue',
  };
  return colors[stockLevel] || 'gray';
};

export const formatSupplyValue = (value: number): string => {
  const numericValue = Number(value) || 0;
  return `R${numericValue.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatSupplyQuantity = (quantity: number, unit: SupplyUnit): string => {
  return `${quantity.toLocaleString()} ${getSupplyUnitLabel(unit)}`;
}; 